import React, { useEffect, useState } from "react";

import { isAxiosError } from "axios";
import { useParams } from "react-router-dom";

import useUpdateFeedResource from "@fartherfinance/frontend/api/Notifications/hooks/useUpdateFeedResource";
import { FeedStatus } from "@fartherfinance/frontend/api/Notifications/Types";
import useGetTaskV3 from "@fartherfinance/frontend/api/Tasks/hooks/useGetTask.v3";
import useGetTaskComments from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import { TaskId } from "@fartherfinance/frontend/api/Types";

import { TASK_COMMENTS_STATIC_QUERY_PARAMS } from "../common";
import TaskDrawerFooter from "../TaskDrawerFooter/TaskDrawerFooter";
import Drawer from "@src/multiCustodian/components/Drawer/Drawer";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Text from "@src/sharedComponents/Typography/Text";

import TaskDetailsFormProviderV3 from "./Components/TaskDetailsFormProvider/TaskDetailsFormProviderV3";
import TaskDetailsUIV3 from "./Components/TaskDetailsUI/TaskDetailsUIV3";

import styles from "./TaskDetails.module.css";

interface TaskDetailsStandaloneProps {
  disableDrawerTransition?: boolean;
  onDrawerExited: () => void;
}

const TaskDetailsStandaloneV3: React.FC<TaskDetailsStandaloneProps> = ({
  disableDrawerTransition = false,
  onDrawerExited,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const { taskId } = useParams<Record<"taskId", TaskId>>();
  const [isInitialRender, setIsInitialRender] = useState(true);

  const auth = useAdvisorRequestAuth();
  const callUpdateFeedResource = useUpdateFeedResource(auth);
  const getTaskQueryV3 = useGetTaskV3(taskId, auth);
  const getTaskCommentsQuery = useGetTaskComments(
    taskId,
    TASK_COMMENTS_STATIC_QUERY_PARAMS,
    auth
  );

  const {
    data: taskDetails,
    isLoading: isTaskDetailsLoading,
    hasError: taskDetailsHasError,
  } = getTaskQueryV3;
  const {
    data: taskComments,
    isLoading: isTaskCommentsLoading,
    hasError: taskCommentsHasError,
  } = getTaskCommentsQuery;
  const { values: comments = [] } = taskComments ?? {};

  const isLoading = isTaskDetailsLoading || isTaskCommentsLoading;
  const hasError = taskDetailsHasError || taskCommentsHasError;
  const isDrawerFooterVisible = !isLoading && !hasError;

  const determineErrorMessage = (): string => {
    if (
      taskDetailsHasError &&
      isAxiosError(getTaskQueryV3.error) &&
      getTaskQueryV3.error.response?.status === 403
    ) {
      return "You don't have access to this task.";
    }

    return "Something went wrong, please try again later.";
  };

  useEffect(() => {
    const updateFeedResource = async (): Promise<void> => {
      const personId = auth?.advisorId;

      if (personId) {
        try {
          await callUpdateFeedResource({
            resourceId: taskId,
            resourceType: "task",
            personId,
            request: { status: FeedStatus.enum.READ },
          });
        } catch {
          // NOTE: fail silently, user should not be aware of this call
        }
      }
    };

    updateFeedResource();

    // NOTE: we just want to run this effect once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer
      isDrawerOpen={isDrawerOpen}
      onClose={() => setIsDrawerOpen(false)}
      SlideProps={{
        onExited: onDrawerExited,
        onEntered: () => setIsInitialRender(false),
        timeout: isInitialRender && disableDrawerTransition ? 0 : undefined,
      }}
      {...(isDrawerFooterVisible && {
        footer: <TaskDrawerFooter taskId={taskId} />,
      })}
      {...(isInitialRender && disableDrawerTransition
        ? {
            ModalProps: {
              componentsProps: { backdrop: { transitionDuration: 0 } },
            },
          }
        : {})}
    >
      {taskDetails && (
        <TaskDetailsFormProviderV3 taskDetails={taskDetails}>
          <TaskDetailsUIV3 taskDetails={taskDetails} taskComments={comments} />
        </TaskDetailsFormProviderV3>
      )}

      {hasError && (
        <div className={styles.errorContainer}>
          <Text variant="title2">{determineErrorMessage()}</Text>
        </div>
      )}

      {isLoading && (
        <div className={styles.loading}>
          <LogoLoadingStill />
        </div>
      )}
    </Drawer>
  );
};

export default TaskDetailsStandaloneV3;
